import { Card, CardContent, CardHeader, CardTitle } from "@shared/components/ui/card";
import { Badge } from "@shared/components/ui/badge";
import { Button } from "@shared/components/ui/button";
import { Progress } from "@shared/components/ui/progress";
import { useQuery } from "@tanstack/react-query";
import {
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle,
  AlertTriangle,
  Package,
  User,
  Calendar,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from '@features/auth/contexts/AuthContext';
import * as pickupService from '@features/pickups/services/pickupService';

const PickupStatusTracking = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "manager";
  const isLivreur = user?.role === "livreur";

  // Fetch pickup analytics
  const { data: analytics, isLoading } = useQuery({
    queryKey: ["pickup-analytics", user?.id],
    queryFn: async () => {
      if (isLivreur) {
        return pickupService.getLivreurPickups("");
      } else {
        return pickupService.getAllPickups();
      }
    },
    select: (data) => {
      const pickups = data?.pickups || [];
      const today = new Date();
      const lastWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

      const thisWeekPickups = pickups.filter(
        (p) => new Date(p.createdAt) >= lastWeek
      );
      const lastWeekPickups = pickups.filter((p) => {
        const createdAt = new Date(p.createdAt);
        const twoWeeksAgo = new Date(
          today.getTime() - 14 * 24 * 60 * 60 * 1000
        );
        return createdAt >= twoWeeksAgo && createdAt < lastWeek;
      });

      const stats = {
        total: pickups.length,
        pending: pickups.filter((p) => p.status === "pending").length,
        approved: pickups.filter((p) => p.status === "approved").length,
        in_transit: pickups.filter((p) => p.status === "in_transit").length,
        delivered: pickups.filter((p) => p.status === "signed").length,
        failed: pickups.filter((p) => p.status === "failed").length,
        assigned: pickups.filter((p) => p.livreur).length,
        unassigned: pickups.filter((p) => !p.livreur).length,
        thisWeek: thisWeekPickups.length,
        lastWeek: lastWeekPickups.length,
      };

      // Calculate trends
      const weeklyTrend =
        stats.lastWeek === 0
          ? 100
          : ((stats.thisWeek - stats.lastWeek) / stats.lastWeek) * 100;

      // Calculate completion rate
      const completionRate =
        stats.total === 0 ? 0 : (stats.delivered / stats.total) * 100;

      return { ...stats, weeklyTrend, completionRate };
    },
  });

  const getStatusData = () => {
    if (!analytics) return [];

    return [
      {
        status: "pending",
        count: analytics.pending,
        color: "bg-orange-500",
        label: t("Pending"),
        icon: Clock,
      },
      {
        status: "approved",
        count: analytics.approved,
        color: "bg-green-500",
        label: t("Approved"),
        icon: CheckCircle,
      },
      {
        status: "in_transit",
        count: analytics.in_transit,
        color: "bg-blue-500",
        label: t("In Transit"),
        icon: Package,
      },
      {
        status: "signed",
        count: analytics.delivered,
        color: "bg-purple-500",
        label: t("Delivered"),
        icon: CheckCircle,
      },
      {
        status: "failed",
        count: analytics.failed,
        color: "bg-red-500",
        label: t("Failed"),
        icon: AlertTriangle,
      },
    ];
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("Pickup Analytics")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const statusData = getStatusData();

  return (
    <div className="space-y-6">
      {/* Analytics Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            {isLivreur ? t("My Pickup Analytics") : t("Pickup Analytics")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Total Pickups */}
            <div className="space-y-2">
              <div className="text-2xl font-bold">{analytics?.total || 0}</div>
              <div className="text-sm text-muted-foreground">
                {t("Total Pickups")}
              </div>
              <div className="flex items-center gap-2">
                {analytics?.weeklyTrend >= 0 ? (
                  <TrendingUp className="h-4 w-4 text-green-500" />
                ) : (
                  <TrendingDown className="h-4 w-4 text-red-500" />
                )}
                <span
                  className={`text-sm ${
                    analytics?.weeklyTrend >= 0
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {Math.abs(analytics?.weeklyTrend || 0).toFixed(1)}%{" "}
                  {t("vs last week")}
                </span>
              </div>
            </div>

            {/* Completion Rate */}
            <div className="space-y-2">
              <div className="text-2xl font-bold">
                {(analytics?.completionRate || 0).toFixed(1)}%
              </div>
              <div className="text-sm text-muted-foreground">
                {t("Completion Rate")}
              </div>
              <Progress
                value={analytics?.completionRate || 0}
                className="h-2"
              />
            </div>

            {/* Assignment Rate (Admin/Manager only) */}
            {(isAdmin || isManager) && (
              <div className="space-y-2">
                <div className="text-2xl font-bold">
                  {analytics?.total === 0
                    ? 0
                    : ((analytics?.assigned / analytics?.total) * 100).toFixed(
                        1
                      )}
                  %
                </div>
                <div className="text-sm text-muted-foreground">
                  {t("Assignment Rate")}
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <User className="h-4 w-4" />
                  <span>
                    {analytics?.assigned} {t("assigned")} / {analytics?.total}{" "}
                    {t("total")}
                  </span>
                </div>
              </div>
            )}

            {/* This Week (Livreur) */}
            {isLivreur && (
              <div className="space-y-2">
                <div className="text-2xl font-bold">
                  {analytics?.thisWeek || 0}
                </div>
                <div className="text-sm text-muted-foreground">
                  {t("This Week")}
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4" />
                  <span>{t("Recent activity")}</span>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Status Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>{t("Status Breakdown")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {statusData.map((item) => {
              const percentage =
                analytics?.total === 0
                  ? 0
                  : (item.count / analytics.total) * 100;
              const IconComponent = item.icon;

              return (
                <div
                  key={item.status}
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`p-2 rounded-lg ${item.color
                        .replace("bg-", "bg-")
                        .replace("-500", "-100")}`}
                    >
                      <IconComponent
                        className={`h-4 w-4 ${item.color.replace(
                          "bg-",
                          "text-"
                        )}`}
                      />
                    </div>
                    <div>
                      <div className="font-medium">{item.label}</div>
                      <div className="text-sm text-muted-foreground">
                        {percentage.toFixed(1)}% {t("of total")}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge
                      variant="outline"
                      className="min-w-[50px] justify-center"
                    >
                      {item.count}
                    </Badge>
                    <div className="w-20">
                      <Progress value={percentage} className="h-2" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="flex gap-2">
        <Button
          variant="outline"
          onClick={() => navigate("/pickups/management")}
          className="flex-1"
        >
          {isLivreur ? t("Manage My Pickups") : t("Manage All Pickups")}
        </Button>
        {(isAdmin || isManager) && (
          <Button
            onClick={() => navigate("/pickups/create")}
            className="flex-1"
          >
            {t("Create New Pickup")}
          </Button>
        )}
      </div>
    </div>
  );
};

export default PickupStatusTracking;
